import { motion } from "framer-motion";
import { Briefcase, MapPin, ArrowRight, ChevronRight } from "lucide-react";

function Meta({ job }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#86868B] mt-1.5 flex-wrap">
      {job.location && <span className="flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
      {job.years_experience && <span>{job.years_experience}</span>}
      <span>{job.employment_type}</span>
    </div>
  );
}

export default function JobListings({ jobs, accent = "#007AFF", layout = "card", embed = false, onSelect }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const wrapperProps = (job) =>
    embed
      ? { href: `${origin}/apply/${job.id}`, target: "_blank", rel: "noreferrer" }
      : { onClick: () => onSelect && onSelect(job) };
  const Tag = embed ? motion.a : motion.button;

  if (jobs.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-black/5 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        <Briefcase size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
        <p className="font-semibold text-lg">No open positions right now</p>
        <p className="text-[#86868B]">Check back soon.</p>
      </div>
    );
  }

  if (layout === "list") {
    return (
      <div className="flex flex-col gap-3">
        {jobs.map((job, i) => (
          <Tag key={job.id} {...wrapperProps(job)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid="careers-job-card"
            className="group text-left w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/5 p-5 hover:shadow-[0_16px_36px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
              <Briefcase size={20} style={{ color: accent }} strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg tracking-tight line-clamp-2">{job.title}</h3>
              <Meta job={job} />
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold shrink-0 group-hover:gap-2.5 transition-all" style={{ color: accent }}>
              Apply <ChevronRight size={16} />
            </span>
          </Tag>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {jobs.map((job, i) => (
        <Tag key={job.id} {...wrapperProps(job)} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
          data-testid="careers-job-card"
          className="group text-left bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/5 p-6 hover:shadow-[0_24px_48px_rgb(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300 cursor-pointer">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${accent}1A` }}>
            <Briefcase size={22} style={{ color: accent }} strokeWidth={1.8} />
          </div>
          <h3 className="font-bold text-xl tracking-tight">{job.title}</h3>
          <Meta job={job} />
          <p className="text-sm text-[#86868B] mt-3 line-clamp-2 leading-relaxed">{job.description}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold mt-4 group-hover:gap-2.5 transition-all" style={{ color: accent }}>
            Apply now <ArrowRight size={15} />
          </span>
        </Tag>
      ))}
    </div>
  );
}
